import { useEffect, useRef } from 'react'
import { useMutation } from '@tanstack/react-query'
import { canResumeImageGenerationJob, imageGenerationErrorMessage, runImageGenerationJob } from './imageGenerationJob'

// Owns only this page's observation and result application, never the server job.
export function useImageGenerationJob(options) {
  const latest = useRef(options)
  latest.current = options
  const active = useRef(null)
  const pendingCalls = useRef({ count: 0 })
  const previousScope = useRef(options.scope)

  useEffect(() => {
    if (previousScope.current !== options.scope) {
      previousScope.current = options.scope
      latest.current.setJob(null)
    }
    return () => {
      active.current?.controller.abort()
      active.current = null
      // A previous scope may still be saving an asset. Its callers release
      // their own record, so they cannot block or unlock this scope's reset.
      pendingCalls.current = { count: 0 }
    }
  }, [options.scope])

  const mutation = useMutation({
    mutationFn: (size) => {
      const { scope, job, setJob, buildInput, createJob, getJob, complete, onSuccess, onError } = latest.current
      // A pending mutation already owns this action; rapid double clicks must
      // not create another paid job before React updates the disabled button.
      if (active.current) return active.current.promise
      const controller = new AbortController()
      const attempt = { controller, promise: null }
      active.current = attempt
      const isCurrent = () => active.current === attempt && latest.current.scope === scope && !controller.signal.aborted
      const existing = canResumeImageGenerationJob(job) && (!job.scope || job.scope === scope) ? job : null
      let tracked = existing
      attempt.promise = (async () => {
        try {
          // Never substitute later form edits for an older job's original input.
          const input = existing ? existing.input || null : buildInput(size)
          if (!existing) setJob(null)
          const completed = await runImageGenerationJob({
            job: existing,
            signal: controller.signal,
            createJob: () => createJob(input),
            getJob,
            onStatus: (status) => {
              tracked = { ...tracked, ...status, scope, input, applied: false }
              if (isCurrent()) setJob(tracked)
            },
          })
          if (!isCurrent()) return
          // The public job API omits inputs. Old drafts without a snapshot receive
          // neutral metadata, rather than claiming the current form generated it.
          const originalInput = input || { prompt: '', size: null, promptFields: {}, presetId: null, referenceAssetIds: [] }
          const result = await complete(completed, originalInput)
          if (!isCurrent()) return
          setJob({ ...tracked, applied: true, observation: null, error: null })
          onSuccess?.(result, originalInput)
          return result
        } catch (error) {
          if (!isCurrent()) return
          if (tracked) setJob({ ...tracked, observation: tracked.terminal ? null : 'paused', error: imageGenerationErrorMessage(error) })
          onError?.(error)
          throw error
        } finally {
          if (active.current === attempt) active.current = null
        }
      })()
      return attempt.promise
    },
  })

  // React Query starts mutationFn asynchronously. Reserve the action at the
  // call so a same-turn Start over cannot turn an already queued resume into
  // a new paid job before active.current is assigned.
  const mutateAsync = (size, callbacks) => {
    const reservation = pendingCalls.current
    reservation.count += 1
    return mutation.mutateAsync(size, callbacks).finally(() => { reservation.count -= 1 })
  }

  return {
    ...mutation,
    mutateAsync,
    mutate: (size, callbacks) => { mutateAsync(size, callbacks).catch(() => {}) },
    discardJob: () => {
      const { job, setJob } = latest.current
      if (pendingCalls.current.count || active.current || !canResumeImageGenerationJob(job)) return false
      // Forget only this observation. The durable job may still finish, and a
      // replacement is created only by a separate, deliberate Generate action.
      latest.current = { ...latest.current, job: null }
      setJob(null)
      mutation.reset()
      return true
    },
  }
}
